import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { createPath } from 'history';
import objectHash from 'object-hash';
import { resolveUrlFilters } from 'utils/urlFilters';
import useURLSearchParams from './useURLSearchParams';

const getFiltersKey = (filters) => objectHash(filters);
const DEFAULT_FILTER_PARAM_SEPARATOR = ',';
const DEFAULT_FILTER_PARAM_PREFIX = '';

const splitFilterParamValue = (paramValue, separator) => {
    if (!paramValue) return [];
    return paramValue
        .split(separator)
        .map((value) => value.trim())
        .filter(Boolean);
};

const useMemoizedFilter = ({ data, filters, handleFilter }) =>
    useMemo(() => {
        const filterEntries = Object.entries(filters);
        if (!(filterEntries.length && data)) return data;
        const filtered = data.filter((item) => {
            for (const [filterName, filterValue] of filterEntries) {
                if (typeof handleFilter[filterName] !== 'function') {
                    throw new Error(
                        `Could not find function for filter type "${filterName}"`,
                    );
                }
                if (!handleFilter[filterName](item, filterValue)) return false;
            }
            return true;
        });
        return filtered.length === data.length ? data : filtered;
    }, [data, filters, handleFilter]);

export const useFilterStateOnly = (initialFilterOption = {}) => {
    const [filters, setFilters] = useState(initialFilterOption);

    const setFilter = useCallback(
        (filterName, filterValue) => {
            const { [filterName]: oldFilterValue, ...restFilters } = filters;
            if (!(filterValue && filterValue.length))
                return setFilters({ ...restFilters });
            return setFilters({ ...restFilters, [filterName]: filterValue });
        },
        [filters, setFilters],
    );

    const resetFilters = useCallback(() => {
        setFilters(initialFilterOption);
    }, [setFilters, initialFilterOption]);

    return {
        filters,
        filtersKey: getFiltersKey(filters),
        setFilter,
        setFilters,
        resetFilters,
    };
};

const useFilter = (data, handleFilter, initialFilterOption = {}) => {
    const filterStateOnly = useFilterStateOnly(initialFilterOption);
    const filteredData = useMemoizedFilter({
        data,
        filters: filterStateOnly.filters,
        handleFilter,
    });
    return {
        filteredData,
        ...filterStateOnly,
    };
};

const convertFilterOptionsToStrings = (filters, filterAccessorsAndOptions) => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
        const { accessor } = filterAccessorsAndOptions[key] || {};
        acc[key] = accessor ? value.map((option) => accessor(option)) : value;
        return acc;
    }, {});
};

const buildFilterParamKey = (key, paramPrefix) =>
    paramPrefix ? `${paramPrefix}${key}` : key;

const createFilterObjectFromSearchParams = (
    filterKeys,
    searchParams,
    separator,
    paramPrefix,
) => {
    return filterKeys.reduce((acc, key) => {
        const paramValue = searchParams.get(
            buildFilterParamKey(key, paramPrefix),
        );
        if (!paramValue) {
            return acc;
        }
        const filteredParamValues =
            key === 'name'
                ? [paramValue]
                : splitFilterParamValue(paramValue, separator);

        if (filteredParamValues.length) {
            acc[key] = filteredParamValues;
        }

        return acc;
    }, {});
};

export const useFilterSearchParamsStateOnly = ({
    supportedFilters,
    initialFilterOption = {},
    filterAccessorsAndOptions,
    separator = DEFAULT_FILTER_PARAM_SEPARATOR,
    paramPrefix = DEFAULT_FILTER_PARAM_PREFIX,
}) => {
    const history = useHistory();
    const location = useLocation();
    const searchParams = useURLSearchParams();

    const searchParamFilters = useMemo(
        () =>
            createFilterObjectFromSearchParams(
                supportedFilters,
                searchParams,
                separator,
                paramPrefix,
            ),
        [supportedFilters, searchParams, separator, paramPrefix],
    );

    const defaultFilters = useMemo(
        () =>
            convertFilterOptionsToStrings(
                initialFilterOption,
                filterAccessorsAndOptions,
            ),
        [initialFilterOption, filterAccessorsAndOptions],
    );
    const filtersByKey = Object.keys(searchParamFilters).length
        ? searchParamFilters
        : defaultFilters;

    // Renames applied in render, so a stale link needs no redirect. Values we
    // cannot map are reported, not acted on — see useStaleFilterNotice.
    const { filters: filtersFromURL, dropped } = resolveUrlFilters(
        filtersByKey,
        filterAccessorsAndOptions,
    );
    const filtersKey = objectHash(filtersFromURL);
    const filters = useMemo(() => filtersFromURL, [filtersKey]);
    const droppedKey = Object.keys(dropped).length
        ? JSON.stringify(dropped)
        : '';
    const staleFilters = useMemo(() => dropped, [droppedKey]);

    const setFilters = useCallback(
        (newFilters) => {
            const nextFiltersByKey = convertFilterOptionsToStrings(
                newFilters,
                filterAccessorsAndOptions,
            );
            const { pathname, hash } = location;
            const searchParams = new URLSearchParams(window.location.search);
            supportedFilters.forEach((key) => {
                searchParams.delete(buildFilterParamKey(key, paramPrefix));
            });

            Object.entries(nextFiltersByKey).forEach(([key, value]) => {
                searchParams.set(
                    buildFilterParamKey(key, paramPrefix),
                    value.join(separator),
                );
            });

            history.replace(
                createPath({
                    pathname,
                    hash,
                    search: `?${searchParams.toString()}`,
                }),
            );
        },
        [filterAccessorsAndOptions, history, location, separator, paramPrefix],
    );

    const setFilter = useCallback(
        (filterName, filterValue) => {
            const { [filterName]: oldFilterValue, ...restFilters } = filters;
            if (!(filterValue && filterValue.length))
                return setFilters({ ...restFilters });
            return setFilters({ ...restFilters, [filterName]: filterValue });
        },
        [filters, setFilters],
    );

    const resetFilters = useCallback(() => {
        setFilters(initialFilterOption);
    }, [setFilters, initialFilterOption]);

    return {
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
        staleFilters,
    };
};

export const useFilterSearchParams = ({
    data,
    handleFilter,
    initialFilterOption = {},
    filterAccessorsAndOptions,
    separator,
    paramPrefix,
}) => {
    const supportedFilters = Object.keys(handleFilter);
    const filterSearchParams = useFilterSearchParamsStateOnly({
        supportedFilters,
        initialFilterOption,
        filterAccessorsAndOptions,
        separator,
        paramPrefix,
    });
    const filteredData = useMemoizedFilter({
        data,
        filters: filterSearchParams.filters,
        handleFilter,
    });
    return {
        filteredData,
        ...filterSearchParams,
    };
};

export default useFilter;
